import { memo, MouseEvent, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Button } from "@design-system/Button/Button";
import { ArrowCornerIcon } from "@icons";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { HistoryRow } from "../../../views/historyViewConfig";
import { HistoryTransactionDetailsRow } from "../views/historyTransactionDetailsViewConfig";
import { isRowDeletedAccrual } from "../historyTransactionRowHelpers";
import styles from "../../cells/HistoryDetailsCells.module.scss";

interface HistoryDetailsCallbacks {
    historyRow: HistoryRow;
    onRowAction?: (row: HistoryTransactionDetailsRow) => void;
}

export const HistoryTransactionsDetailsArrowCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        HistoryTransactionDetailsRow,
        string
    >) => {
        const meta = useViewTableMeta<HistoryDetailsCallbacks, unknown>(table);
        const onRowAction = meta.callbacks?.onRowAction;

        const isDeletedAccrual = isRowDeletedAccrual(row.original);

        const handleClick = useCallback(
            (event: MouseEvent<HTMLButtonElement>) => {
                markEventBlocked(event);
                onRowAction?.(row.original);
            },
            [onRowAction, row.original],
        );

        if (isDeletedAccrual) {
            return <ViewTableCell className={styles.actions} />;
        }

        return (
            <ViewTableCell className={styles.actions}>
                <div className={styles.actionsButtons}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleClick}
                        title="View details"
                        aria-label="View details"
                        iconOnly
                        className={tableStyles.showOnHover}
                    >
                        <ArrowCornerIcon />
                    </Button>
                </div>
            </ViewTableCell>
        );
    },
);

HistoryTransactionsDetailsArrowCell.displayName =
    "HistoryTransactionsDetailsArrowCell";
